import React, { useContext, useEffect, useMemo, useState } from "react";

import Loading from "../../components/Loading";
import { SettingsContext } from "../../contexts/index.jsx";
import { UserContext } from "../../contexts/User.jsx";
import { ACCOUNTING, canAccounting } from "../../helpers/accountingAccess";
import { useTransactionsPnl } from "../../hooks/dailyRecap/useTransactionsPnl";
import { useStockSnapshot, toggleStockVerified } from "../../hooks/dailyRecap/useStockSnapshot";
import { useXeroPurchaseOrders, useXeroInvoices } from "../../hooks/dailyRecap/useXeroAccounting";
import StockColumn from "./StockColumn";
import XeroDocsSection from "./XeroDocsSection";

import "./accounting.scss";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const gbp = (v) =>
  v === null || v === undefined
    ? "—"
    : new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        maximumFractionDigits: 0,
      }).format(v);

// Mobile/tablet breakpoint for the stock columns — must stay in sync with the
// `max-width: 1100px` query in accounting.scss (`.acct-cols`).
const STACK_BREAKPOINT = "(max-width: 1100px)";

const useStackedColumns = () => {
  const [stacked, setStacked] = useState(
    () => typeof window !== "undefined" && window.matchMedia(STACK_BREAKPOINT).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(STACK_BREAKPOINT);
    const onChange = (e) => setStacked(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return stacked;
};

const AccountingBody = () => {
  const settings = useContext(SettingsContext);
  const { user } = useContext(UserContext);
  const canVoid = canAccounting(user, ACCOUNTING.VOID_INVOICE);
  const qp = settings?.iframeQueryParams || "";
  const withQp = (path) => (qp ? `${path}?${qp}` : path);
  const now = new Date();

  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y, y - 1, y - 2];
  }, []);

  // KPIs reuse the P&L endpoint so the tiles match /pnl exactly.
  const { data: pnl, isLoading: pnlLoading } = useTransactionsPnl(year, month);
  const totals = pnl?.totals || {};

  const { data: poData, isLoading: poLoading, refetch: refetchPos } = useXeroPurchaseOrders(year, month);
  const { data: invData, isLoading: invLoading, refetch: refetchInvoices } = useXeroInvoices(year, month);

  const { data: snap, isLoading: snapLoading, isFetching, refetch: refetchSnap } = useStockSnapshot(year, month);
  const opening = snap?.opening?.items || [];
  const purchases = snap?.purchases?.items || [];
  const closing = snap?.closing?.items || [];
  const movement = snap?.movement || {};

  const mv = (k) => movement?.[k] || { count: 0, value: 0 };

  // Reconciling roll-forward: Opening + Purchases − Sold = Closing.
  // Pending acquisitions (no listing yet) are NOT stock, so they're excluded
  // from the Purchases total and shown separately below.
  const sum = (arr) => arr.reduce((s, x) => s + (x.cost || 0), 0);
  const pendingItems = purchases.filter((p) => p.via === "pending_listing");
  const mainPurchases = purchases.filter((p) => p.via !== "pending_listing");

  // Stacked (mobile/tablet) layout turns the three columns into an accordion
  // with a single section open at a time; desktop always shows all three.
  const stacked = useStackedColumns();
  const [openCol, setOpenCol] = useState("opening");
  const toggleCol = (key) => setOpenCol((cur) => (cur === key ? null : key));
  const colProps = (key) => ({
    collapsible: stacked,
    isOpen: openCol === key,
    onToggle: () => toggleCol(key),
  });

  // Persisted per-period verified sign-off (stored backend-side, shared).
  const verified = !!snap?.verified;
  const [verifying, setVerifying] = useState(false);
  const onToggleVerify = async () => {
    setVerifying(true);
    try {
      await toggleStockVerified(year, month);
      await refetchSnap();
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="acct-page">
      <h2 className="daily-recap__section-title acct-page__title">FINANCIAL SUMMARY</h2>
      <div className="acct-toolbar">
        <div className="acct-toolbar__left">
          <select
            className="acct-select"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            aria-label="Month"
          >
            {MONTHS.map((m, i) => (
              <option
                key={m}
                value={i + 1}
                disabled={year === now.getFullYear() && i + 1 > now.getMonth() + 1}
              >
                {m}
              </option>
            ))}
          </select>
          <select
            className="acct-select"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            aria-label="Year"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {isFetching && <span className="acct-loading">Updating…</span>}
        </div>
      </div>

      <div className="acct-kpis">
        <div className="acct-kpi"><span>Sold</span><b>{pnlLoading ? "—" : (totals.watches_sold ?? 0)}</b></div>
        <div className="acct-kpi"><span>Revenue</span><b>{gbp(totals.total_sell)}</b></div>
        <div className="acct-kpi"><span>Margin</span><b className={(totals.gross_margin ?? 0) < 0 ? "acct-neg" : "acct-accent"}>{gbp(totals.gross_margin)}</b></div>
        <div className="acct-kpi"><span>Cost/watch</span><b>{gbp(totals.cost_per_watch)}</b></div>
        <div className="acct-kpi"><span>Profit/watch</span><b className={(totals.profit_per_watch ?? 0) < 0 ? "acct-neg" : "acct-accent"}>{gbp(totals.profit_per_watch)}</b></div>
      </div>

      <div className="acct-actions">
        <a className="acct-pnl-btn" href={withQp("/pnl")} target="_blank" rel="noopener noreferrer">
          VIEW P&amp;L
        </a>
        <a className="acct-pnl-btn" href={withQp("/reconciliation")} target="_blank" rel="noopener noreferrer">
          RECONCILE
        </a>
      </div>

      <div className="acct-stock">
        <div className="acct-stock__head">
          {/* Owned-stock roll-forward: Opening + Added − Sold(owned) = Closing
              (cost basis). "Sold" here is OWNED stock that left inventory. The
              full sold-source split (owned/consignment/merchant) reconciles to
              the period's total sales below. */}
          <div className="acct-tieout-group">
            <div className="acct-tieout">
              <span className="acct-tieout__term"><i>Opening</i> {opening.length} · {gbp(sum(opening))}</span>
              <span className="acct-tieout__op">+</span>
              <span className="acct-tieout__term"><i>Purchases</i> {mainPurchases.length} · {gbp(sum(mainPurchases))}</span>
              <span className="acct-tieout__op">−</span>
              <span className="acct-tieout__term"><i>Sold</i> {mv("sold_total").count} · {gbp(mv("sold_total").value)}</span>
              <span className="acct-tieout__op">=</span>
              <span className="acct-tieout__term acct-tieout__term--total"><i>Closing</i> {closing.length} · {gbp(sum(closing))}</span>
              <button
                type="button"
                className={`acct-verify ${verified ? "acct-verify--on" : ""}`}
                onClick={onToggleVerify}
                disabled={verifying}
                title={verified ? "Verified — click to unlock" : "Mark this period as verified"}
              >
                <span className="acct-verify__icon">{verified ? "🔒" : "🔓"}</span>
                {verifying ? "Saving…" : verified ? "Verified" : "Verify"}
              </button>
            </div>
            <div className="acct-soldsplit">
              <span className="acct-soldsplit__total">
                {mv("sold").count + mv("sold_consignment").count + mv("sold_merchant").count} sold this period
              </span>
              <span className="acct-soldsplit__buckets">
                Owned {mv("sold").count} · Consignment {mv("sold_consignment").count} · Merchant {mv("sold_merchant").count}
              </span>
            </div>
          </div>
        </div>

        {snapLoading ? (
          <Loading />
        ) : (
          <div className={`acct-cols ${stacked ? "acct-cols--stacked" : ""}`}>
            <StockColumn title="Opening Stock" items={opening} variant="stock" {...colProps("opening")} />
            <StockColumn title="Purchases" items={mainPurchases} variant="purchases" {...colProps("purchases")} />
            <StockColumn title="Closing Stock" items={closing} variant="stock" {...colProps("closing")} />
          </div>
        )}

        {!snapLoading && pendingItems.length > 0 && (
          <div className="acct-pending">
            <p className="acct-pending__note">Not yet stock — excluded from the roll-forward above.</p>
            <StockColumn
              title="Pending Acquisitions"
              items={pendingItems}
              variant="purchases"
              {...colProps("pending")}
            />
          </div>
        )}
      </div>

      <div className="acct-xero">
        <XeroDocsSection
          title="Purchase Orders"
          kind="po"
          flagLabel="Present in Xero, no sell request in RC"
          data={poData}
          isLoading={poLoading}
          year={year}
          month={month}
          canVoid={canVoid}
          refetch={refetchPos}
        />
        <XeroDocsSection
          title="Invoices"
          kind="invoice"
          flagLabel="Present in Xero, no order in RC"
          data={invData}
          isLoading={invLoading}
          year={year}
          month={month}
          canVoid={canVoid}
          refetch={refetchInvoices}
        />
      </div>
    </div>
  );
};

// Gate the Accounting tab on the "Accounting tab view" grant
// (master admins pass by default; other roles need the explicit grant).
const AccountingView = () => {
  const { user } = useContext(UserContext);
  if (!canAccounting(user, ACCOUNTING.TAB)) {
    return <div className="acct-restricted">Access restricted</div>;
  }
  return <AccountingBody />;
};

export default AccountingView;
